import React, { useEffect, useRef, useState } from "react";
import axios from "../api/axiosInstance";
import { usePmsTheme } from "../Components/PmsThemeContext";
import "./TaxSettings.css";

const COPY = {
  en: {
    title: "Tax types", intro: "Configure this hotel's taxes, percentages and validity dates.",
    empty: "No tax types have been configured yet.", loading: "Loading taxes…",
    add: "Add tax", edit: "Edit", deactivate: "Deactivate", reactivate: "Reactivate",
    newTitle: "New tax", editTitle: "Edit tax", name: "Name", percentage: "Percentage (%)",
    from: "Valid from", to: "Valid until", optional: "Optional", save: "Save tax",
    cancel: "Cancel", saving: "Saving…", active: "Active", inactive: "Inactive",
    unlimited: "No date limit", since: "From", until: "Until", retry: "Retry",
    readOnly: "You can view tax types, but you do not have permission to change them.",
    saved: "Tax saved.", deactivated: "Tax deactivated.", reactivated: "Tax reactivated.",
    loadError: "The tax types could not be loaded.", saveError: "The change could not be saved. Please try again.",
    schemaError: "Tax configuration is not available yet. Please contact your administrator.",
    invalidBu: "Select a valid Business Unit to view its taxes.",
    nameError: "Enter a name with up to 120 characters.",
    percentageError: "Enter a percentage from 0 to 100, with up to 4 decimal places.",
    dateError: "Enter a valid date.", rangeError: "The end date cannot be before the start date.",
    percentageHelp: "From 0 to 100, with up to 4 decimal places.",
  },
  es: {
    title: "Tipos de impuestos", intro: "Configura los impuestos de este hotel, sus porcentajes y fechas de vigencia.",
    empty: "Todavía no hay tipos de impuestos configurados.", loading: "Cargando impuestos…",
    add: "Añadir impuesto", edit: "Editar", deactivate: "Desactivar", reactivate: "Reactivar",
    newTitle: "Nuevo impuesto", editTitle: "Editar impuesto", name: "Nombre", percentage: "Porcentaje (%)",
    from: "Vigente desde", to: "Vigente hasta", optional: "Opcional", save: "Guardar impuesto",
    cancel: "Cancelar", saving: "Guardando…", active: "Activo", inactive: "Inactivo",
    unlimited: "Sin límite de fechas", since: "Desde", until: "Hasta", retry: "Reintentar",
    readOnly: "Puedes consultar los impuestos, pero no tienes permiso para modificarlos.",
    saved: "Impuesto guardado.", deactivated: "Impuesto desactivado.", reactivated: "Impuesto reactivado.",
    loadError: "No se han podido cargar los tipos de impuestos.", saveError: "No se ha podido guardar el cambio. Inténtalo de nuevo.",
    schemaError: "La configuración de impuestos todavía no está disponible. Contacta con tu administrador.",
    invalidBu: "Selecciona una unidad de negocio válida para consultar sus impuestos.",
    nameError: "Introduce un nombre de hasta 120 caracteres.",
    percentageError: "Introduce un porcentaje entre 0 y 100, con un máximo de 4 decimales.",
    dateError: "Introduce una fecha válida.", rangeError: "La fecha final no puede ser anterior a la inicial.",
    percentageHelp: "Entre 0 y 100, con un máximo de 4 decimales.",
  },
  ca: {
    title: "Tipus d'impostos", intro: "Configura els impostos d'aquest hotel, els percentatges i les dates de vigència.",
    empty: "Encara no hi ha tipus d'impostos configurats.", loading: "Carregant impostos…",
    add: "Afegir impost", edit: "Editar", deactivate: "Desactivar", reactivate: "Reactivar",
    newTitle: "Nou impost", editTitle: "Editar impost", name: "Nom", percentage: "Percentatge (%)",
    from: "Vigent des de", to: "Vigent fins a", optional: "Opcional", save: "Desar impost",
    cancel: "Cancel·lar", saving: "Desant…", active: "Actiu", inactive: "Inactiu",
    unlimited: "Sense límit de dates", since: "Des de", until: "Fins a", retry: "Tornar-ho a provar",
    readOnly: "Pots consultar els impostos, però no tens permís per modificar-los.",
    saved: "Impost desat.", deactivated: "Impost desactivat.", reactivated: "Impost reactivat.",
    loadError: "No s'han pogut carregar els tipus d'impostos.", saveError: "No s'ha pogut desar el canvi. Torna-ho a provar.",
    schemaError: "La configuració d'impostos encara no està disponible. Contacta amb el teu administrador.",
    invalidBu: "Selecciona una unitat de negoci vàlida per consultar els seus impostos.",
    nameError: "Introdueix un nom de fins a 120 caràcters.",
    percentageError: "Introdueix un percentatge entre 0 i 100, amb un màxim de 4 decimals.",
    dateError: "Introdueix una data vàlida.", rangeError: "La data final no pot ser anterior a la inicial.",
    percentageHelp: "Entre 0 i 100, amb un màxim de 4 decimals.",
  },
};

const emptyForm = () => ({ name: "", percentage: "", valid_from: "", valid_to: "" });
const isValidDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || value.startsWith("0000")) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

export function validateTaxForm(form, copy = COPY.en) {
  const errors = {};
  const name = String(form.name || "").trim();
  const percentage = String(form.percentage ?? "").trim().replace(",", ".");
  if (!name || name.length > 120) errors.name = copy.nameError;
  if (!/^\d+(?:\.\d{1,4})?$/.test(percentage) || Number(percentage) > 100) {
    errors.percentage = copy.percentageError;
  }
  for (const field of ["valid_from", "valid_to"]) {
    if (form[field] && !isValidDate(form[field])) errors[field] = copy.dateError;
  }
  if (!errors.valid_from && !errors.valid_to && form.valid_from && form.valid_to && form.valid_from > form.valid_to) {
    errors.valid_to = copy.rangeError;
  }
  return errors;
}

const errorMessage = (error, copy, fallback) =>
  error?.response?.data?.code === "TAX_SETTINGS_SCHEMA_NOT_READY" ? copy.schemaError : fallback;

export default function TaxSettings({ businessUnitId, canManage }) {
  const { languageId, language } = usePmsTheme();
  const copy = COPY[languageId] || COPY.en;
  const locale = language?.locale || ({ es: "es-ES", ca: "ca-ES" }[languageId] || "en-GB");
  const bu = Number(businessUnitId);
  const validBu = Number.isSafeInteger(bu) && bu > 0;
  const currentBu = useRef(bu);
  currentBu.current = bu;
  const [loadedBu, setLoadedBu] = useState(null);
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const mutation = useRef(null);
  const copyRef = useRef(copy);
  copyRef.current = copy;

  useEffect(() => {
    const controller = new AbortController();
    setLoadedBu(bu);
    setLoading(true);
    setLoadError("");
    setTaxes([]);
    setForm(null);
    setEditingId(null);
    setErrors({});
    setFeedback(null);
    setBusy(false);
    mutation.current = null;
    if (validBu) {
      axios.get("/api/tax-settings", {
        params: { business_unit_id: bu }, signal: controller.signal,
      }).then(({ data }) => {
        if (controller.signal.aborted || currentBu.current !== bu) return;
        if (!Array.isArray(data?.taxes) || data.taxes.some((tax) =>
          tax.business_unit_id != null && Number(tax.business_unit_id) !== bu)) {
          throw new Error("Invalid tax configuration response");
        }
        setTaxes(data.taxes);
      }).catch((error) => {
        if (controller.signal.aborted || currentBu.current !== bu) return;
        setLoadError(errorMessage(error, copyRef.current, copyRef.current.loadError));
      }).finally(() => {
        if (!controller.signal.aborted && currentBu.current === bu) setLoading(false);
      });
    } else setLoading(false);
    return () => {
      controller.abort();
      mutation.current?.abort();
    };
  }, [bu, validBu, reload]);

  const ready = validBu && loadedBu === bu && !loading && !loadError;
  const startForm = (tax = null) => {
    if (!canManage || !ready || mutation.current) return;
    setEditingId(tax?.id ?? null);
    setForm(tax ? {
      name: tax.name, percentage: String(tax.percentage),
      valid_from: tax.valid_from || "", valid_to: tax.valid_to || "",
    } : emptyForm());
    setErrors({});
    setFeedback(null);
  };

  const persist = async (payload, taxId, successMessage, closeForm) => {
    if (!canManage || !ready || mutation.current) return;
    const controller = new AbortController();
    mutation.current = controller;
    setBusy(true);
    setFeedback(null);
    try {
      const url = `/api/tax-settings/${bu}${taxId == null ? "" : `/${taxId}`}`;
      const { data } = await axios[taxId == null ? "post" : "put"](url, payload, { signal: controller.signal });
      if (controller.signal.aborted || currentBu.current !== bu) return;
      if (!data?.tax?.id || (data.tax.business_unit_id != null && Number(data.tax.business_unit_id) !== bu)) {
        throw new Error("Invalid saved tax response");
      }
      setTaxes((previous) => {
        const existing = previous.some((tax) => tax.id === data.tax.id);
        return existing ? previous.map((tax) => tax.id === data.tax.id ? data.tax : tax) : [...previous, data.tax];
      });
      if (closeForm) {
        setForm(null);
        setEditingId(null);
        setErrors({});
      }
      setFeedback({ type: "success", message: successMessage });
    } catch (error) {
      if (!controller.signal.aborted && currentBu.current === bu) {
        setFeedback({ type: "error", message: errorMessage(error, copy, copy.saveError) });
      }
    } finally {
      if (mutation.current === controller) {
        mutation.current = null;
        if (!controller.signal.aborted && currentBu.current === bu) setBusy(false);
      }
    }
  };

  const save = (event) => {
    event.preventDefault();
    if (!form || !canManage || !ready || mutation.current) return;
    const validation = validateTaxForm(form, copy);
    setErrors(validation);
    if (Object.keys(validation).length) return;
    persist({
      name: form.name.trim(), percentage: String(Number(form.percentage.trim().replace(",", "."))),
      valid_from: form.valid_from || null, valid_to: form.valid_to || null,
    }, editingId, copy.saved, true);
  };

  const dateLabel = (value) => new Intl.DateTimeFormat(locale, { timeZone: "UTC" }).format(new Date(`${value}T00:00:00Z`));
  const validity = (tax) => [
    tax.valid_from ? `${copy.since} ${dateLabel(tax.valid_from)}` : "",
    tax.valid_to ? `${copy.until} ${dateLabel(tax.valid_to)}` : "",
  ].filter(Boolean).join(" · ") || copy.unlimited;
  const input = (field, label, type = "text") => (
    <div className={`taxSettings__field taxSettings__field--${field}`}>
      <label htmlFor={`tax-settings-${field}`}>{label}{type === "date" ? <span> ({copy.optional})</span> : null}</label>
      <input
        id={`tax-settings-${field}`} name={field} type={type} value={form[field]}
        inputMode={field === "percentage" ? "decimal" : undefined}
        maxLength={field === "name" ? 120 : undefined}
        min={type === "date" ? "0001-01-01" : undefined}
        max={type === "date" ? "9999-12-31" : undefined}
        required={type !== "date"} aria-invalid={Boolean(errors[field])}
        aria-describedby={errors[field] ? `tax-settings-${field}-error` : field === "percentage" ? "tax-settings-percentage-help" : undefined}
        onChange={(event) => {
          const value = event.target.value;
          setForm((previous) => ({ ...previous, [field]: value }));
          setErrors((previous) => ({ ...previous, [field]: undefined }));
        }}
      />
      {errors[field] ? <span className="taxSettings__fieldError" id={`tax-settings-${field}-error`}>{errors[field]}</span> : null}
      {field === "percentage" ? <small id="tax-settings-percentage-help">{copy.percentageHelp}</small> : null}
    </div>
  );

  return (
    <section className="taxSettings" aria-labelledby="tax-settings-title">
      <div className="taxSettings__heading">
        <div><h2 id="tax-settings-title">{copy.title}</h2><p>{copy.intro}</p></div>
        {canManage && ready && !form ? <button className="taxSettings__button taxSettings__button--primary" type="button" disabled={busy} onClick={() => startForm()}>{copy.add}</button> : null}
      </div>
      {!validBu ? <p role="status">{copy.invalidBu}</p> : loading || loadedBu !== bu ? <p role="status">{copy.loading}</p> : loadError ? (
        <div className="taxSettings__feedback taxSettings__feedback--error" role="alert">
          <span>{loadError}</span><button className="taxSettings__button" type="button" onClick={() => setReload((value) => value + 1)}>{copy.retry}</button>
        </div>
      ) : (
        <>
          {!canManage ? <p className="taxSettings__muted">{copy.readOnly}</p> : null}
          {feedback ? <p className={`taxSettings__feedback taxSettings__feedback--${feedback.type}`} role={feedback.type === "error" ? "alert" : "status"}>{feedback.message}</p> : null}
          {form && canManage ? (
            <form className="taxSettings__form" onSubmit={save} noValidate>
              <h3>{editingId == null ? copy.newTitle : copy.editTitle}</h3>
              <fieldset disabled={busy}>
                {input("name", copy.name)}{input("percentage", copy.percentage)}
                {input("valid_from", copy.from, "date")}{input("valid_to", copy.to, "date")}
              </fieldset>
              <div className="taxSettings__actions">
                <button type="submit" className="taxSettings__button taxSettings__button--primary" disabled={busy}>{busy ? copy.saving : copy.save}</button>
                <button type="button" className="taxSettings__button" disabled={busy} onClick={() => { setForm(null); setErrors({}); setFeedback(null); }}>{copy.cancel}</button>
              </div>
            </form>
          ) : null}
          {taxes.length ? <ul className="taxSettings__list">
            {taxes.map((tax) => <li className="taxSettings__row" key={tax.id}>
              <div className="taxSettings__details"><h3>{tax.name}</h3><p>{validity(tax)}</p></div>
              <strong className="taxSettings__percentage">{new Intl.NumberFormat(locale, { maximumFractionDigits: 4 }).format(Number(tax.percentage))} %</strong>
              <span className={`taxSettings__state${tax.is_active ? " taxSettings__state--active" : ""}`}>{tax.is_active ? copy.active : copy.inactive}</span>
              {canManage ? <div className="taxSettings__actions">
                <button className="taxSettings__button" type="button" disabled={busy || Boolean(form)} aria-label={`${copy.edit} ${tax.name}`} onClick={() => startForm(tax)}>{copy.edit}</button>
                <button className="taxSettings__button" type="button" disabled={busy || Boolean(form)} aria-label={`${tax.is_active ? copy.deactivate : copy.reactivate} ${tax.name}`} onClick={() => persist({ is_active: !tax.is_active }, tax.id, tax.is_active ? copy.deactivated : copy.reactivated, false)}>{tax.is_active ? copy.deactivate : copy.reactivate}</button>
              </div> : null}
            </li>)}
          </ul> : <p className="taxSettings__empty">{copy.empty}</p>}
        </>
      )}
    </section>
  );
}
